import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Input from "../../CoreComponent/Input";
import Checkbox from "../../CoreComponent/Checkbox";
import "./style.scss";
import { useAuth } from "../../common/AuthContext";
import toast from "react-hot-toast";
import SimpleLabelValue from "../SimpleLabelValue";

const EXTRA_PRICE_PER_GUEST = 50;
const LS_KEY_EXTRA_GUESTS = "dinner_extra_guest_names_v1";

const safeParseJSON = (value, fallback) => {
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const DinnerDetails = () => {
  const [dinnerDetail, setDinnerDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [attending, setAttending] = useState(false);
  const [hasGuest, setHasGuest] = useState(false);

  const [defaultGuestCount, setDefaultGuestCount] = useState(0);
  const [defaultGuestNames, setDefaultGuestNames] = useState([]);

  const [extraGuestNames, setExtraGuestNames] = useState(() => {
    const saved = localStorage.getItem(LS_KEY_EXTRA_GUESTS);
    return saved ? safeParseJSON(saved, []) : [];
  });

  const [dinnerInvoice, setDinnerInvoice] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [attendeeId, setAttendeeId] = useState(null);

  const { myConferenceId } = useAuth();
  const BaseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    localStorage.setItem(LS_KEY_EXTRA_GUESTS, JSON.stringify(extraGuestNames));
  }, [extraGuestNames]);

  const companionPrice = useMemo(() => {
    const cleanExtra = extraGuestNames.map((n) => n.trim()).filter(Boolean);
    return cleanExtra.length * EXTRA_PRICE_PER_GUEST;
  }, [extraGuestNames]);

  // Server invoice se actual payable amount
  const payableAmount = useMemo(() => {
    return Number(dinnerInvoice?.companion_price || 0);
  }, [dinnerInvoice]);

  const getDinnerInvoice = async () => {
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(`${BaseUrl}/dinner/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDinnerInvoice(response.data?.dinner_attendee || null);
      setAttendeeId(response.data?.dinner_attendee?.id || null);
      setInvoice(response.data?.invoice || null);
    } catch (error) {
      console.error("Error fetching invoice data", error);
      setDinnerInvoice(null);
      setInvoice(null);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("token");

    if (!attendeeId) {
      toast.error("Dinner attendance record not found.");
      return;
    }

    try {
      setSubmitLoading(true);

      await axios.delete(`${BaseUrl}/dinner-attendees/${attendeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Attendance canceled successfully!");

      localStorage.removeItem(LS_KEY_EXTRA_GUESTS);
      setExtraGuestNames([]);
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Unable to cancel dinner participation."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    try {
      const amount = Number(dinnerInvoice?.companion_price || 0);

      if (amount <= 0) {
        toast.error("No payment is required.");
        return;
      }

      setSubmitLoading(true);

      const response = await axios.post(
        "https://backendnew.eventscons.com/api/create-payment",
        {
          amount,
          return_url: "https://test.com",
          cancel_url: "https://test.com",
        }
      );

      if (response.data?.approval_url) {
        window.location.href = response.data.approval_url;
        return;
      }

      toast.error("Approval URL not found in response");
    } catch (error) {
      console.error("Payment Error:", error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const fetchDinnerInfo = async () => {
    const token = localStorage.getItem("token");

    try {
      const res = await axios.get(
        "https://backendnew.eventscons.com/api/speakers/dinnerinfo",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const count = Number(res.data?.conference_dinner_persons || 0);

      setDefaultGuestCount(count);
      setDefaultGuestNames(Array(count).fill(""));
    } catch (err) {
      console.error("Error fetching dinnerinfo:", err);
    }
  };

  const fetchDinnerDetails = async () => {
    try {
      const response = await axios.get(
        `${BaseUrl}/dinners/conference/${myConferenceId}`
      );

      setDinnerDetail(response?.data?.dinner_detail || null);
    } catch (error) {
      console.error("Error fetching dinner details", error);
      setDinnerDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!myConferenceId) {
      setLoading(false);
      return;
    }

    fetchDinnerDetails();
    fetchDinnerInfo();
    getDinnerInvoice();
  }, [myConferenceId]);

  const addDinnerAttendees = async () => {
    const token = localStorage.getItem("token");

    try {
      setSubmitLoading(true);

      const cleanDefault = defaultGuestNames
        .map((n) => n.trim())
        .filter(Boolean);

      const cleanExtra = extraGuestNames.map((n) => n.trim()).filter(Boolean);

      const totalGuests = [...cleanDefault, ...cleanExtra];

      if (hasGuest && totalGuests.length === 0) {
        toast.error("Please enter at least one guest name.");
        return;
      }

      const fd = new FormData();

      if (totalGuests.length === 0) {
        fd.append("companion_name[]", "");
      } else {
        totalGuests.forEach((guest) => {
          fd.append("companion_name[]", guest);
        });
      }

      fd.append(
        "companion_price",
        String(cleanExtra.length * EXTRA_PRICE_PER_GUEST)
      );

      fd.append("conference_id", String(myConferenceId));

      await axios.post(`${BaseUrl}/dinner/attendees`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Dinner attendance confirmed successfully!");

      await getDinnerInvoice();

      localStorage.removeItem(LS_KEY_EXTRA_GUESTS);
      setExtraGuestNames([]);
    } catch (error) {
      console.error("Dinner attendee error:", error);
      console.log("Server response:", error.response?.data);

      toast.error(
        error.response?.data?.message ||
          "Error adding dinner attendees. Please try again."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAddExtraGuest = () => {
    setExtraGuestNames((previous) => [...previous, ""]);
  };

  const handleRemoveExtraGuest = (index) => {
    setExtraGuestNames((previous) =>
      previous.filter((_, guestIndex) => guestIndex !== index)
    );
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="dinner-details3">
      <div className="dinner-details-container">
        <h2 className="dinner-title">Gala Dinner Details</h2>

        {dinnerDetail ? (
          <>
            <div className="dinner-detail-section">
              <SimpleLabelValue
                label="Date"
                value={new Date(
                  dinnerDetail.dinner_date
                ).toLocaleDateString()}
              />

              <SimpleLabelValue
                label="Restaurant"
                value={dinnerDetail.restaurant_name}
              />

              <SimpleLabelValue
                label="Location"
                value={dinnerDetail.location}
              />

              <SimpleLabelValue
                label="Gathering Place"
                value={dinnerDetail.gathering_place}
              />

              <SimpleLabelValue
                label="Transportation"
                value={dinnerDetail.transportation_method}
              />

              <SimpleLabelValue
                label="Gathering Time"
                value={dinnerDetail.gathering_time}
              />

              <SimpleLabelValue
                label="Dinner Time"
                value={dinnerDetail.dinner_time}
              />

              <SimpleLabelValue
                label="Duration"
                value={`${dinnerDetail.duration} minutes`}
              />

              <SimpleLabelValue
                label="Dress Code"
                value={dinnerDetail.dress_code}
              />
            </div>

            {!dinnerInvoice && !invoice ? (
              <div className="attendance-section">
                <h3>Attendance Confirmation</h3>

                <Checkbox
                  label="Will you be attending?"
                  checkboxValue={attending}
                  setCheckboxValue={setAttending}
                  className="form-checkbox"
                />

                {attending && (
                  <div>
                    <p>
                      All dinner information will be sent via WhatsApp by the
                      organizing company.
                    </p>

                    <Checkbox
                      label="Do you have guests?"
                      checkboxValue={hasGuest}
                      setCheckboxValue={setHasGuest}
                      className="form-checkbox"
                    />

                    {hasGuest && (
                      <div className="guest-fields">
                        <p>
                          You are allowed to add up to {defaultGuestCount} guests
                        </p>

                        {defaultGuestNames.map((name, idx) => (
                          <div
                            key={`default-${idx}`}
                            className="guest-field-row"
                          >
                            <Input
                              className="input-cont-dinner"
                              type="text"
                              placeholder={`Enter Guest ${idx + 1} Name`}
                              inputValue={name}
                              setInputValue={(val) => {
                                setDefaultGuestNames((previous) => {
                                  const updated = [...previous];
                                  updated[idx] = val;
                                  return updated;
                                });
                              }}
                            />
                          </div>
                        ))}

                        {extraGuestNames.map((name, idx) => (
                          <div
                            key={`extra-${idx}`}
                            className="guest-field-row"
                            style={{ display: "flex" }}
                          >
                            <Input
                              type="text"
                              placeholder={`Extra Guest ${idx + 1} Name`}
                              inputValue={name}
                              setInputValue={(val) => {
                                setExtraGuestNames((previous) => {
                                  const updated = [...previous];
                                  updated[idx] = val;
                                  return updated;
                                });
                              }}
                            />

                            <button
                              type="button"
                              className="delete-btn"
                              onClick={() => handleRemoveExtraGuest(idx)}
                              style={{
                                width: "fit-content",
                                marginLeft: "10px",
                                padding: "5px 10px",
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="add-btn"
                          onClick={handleAddExtraGuest}
                          style={{
                            width: "fit-content",
                            marginTop: "10px",
                            padding: "5px 10px",
                          }}
                        >
                          ➕ Add Extra Guest
                        </button>

                        <div
                          className="companion-price"
                          style={{ marginTop: 16, fontWeight: 600 }}
                        >
                          Companion Price: $ {companionPrice.toFixed(2)}
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      style={{ backgroundColor: "#9B1321" }}
                      className="submit-btn"
                      onClick={addDinnerAttendees}
                      disabled={submitLoading}
                    >
                      {submitLoading ? "Processing..." : "Confirm Attendance"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="invoice-section">
                {dinnerInvoice && (
                  <>
                    <SimpleLabelValue
                      label="Companion Name(s)"
                      value={dinnerInvoice.companion_name || "-"}
                    />

                    <SimpleLabelValue
                      label="Companion Price (total)"
                      value={`${Number(
                        dinnerInvoice.companion_price || 0
                      ).toFixed(2)} $`}
                    />
                  </>
                )}

                {invoice && (
                  <SimpleLabelValue
                    label="Invoice Status"
                    value={invoice.status}
                  />
                )}

                <div style={{ display: "flex", gap: "20px" }}>
                  {payableAmount > 0 && (
                    <button
                      type="button"
                      onClick={handleCreatePayment}
                      className="pay-now-btn"
                      disabled={submitLoading}
                    >
                      {submitLoading
                        ? "Processing..."
                        : `Pay Now ($ ${payableAmount.toFixed(2)})`}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleDelete}
                    className="pay-now-btn"
                    disabled={submitLoading}
                  >
                    Cancel Participation in Dinner
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p>No dinner details available.</p>
        )}
      </div>
    </div>
  );
};

export default DinnerDetails;